import { useEffect, useRef, useState } from 'react';

interface Options {
  // When false the hook is inert (admins/creators bypass anti-skip).
  enabled?: boolean;
  // Resetting key (e.g., lesson.id / media-id) — clears the watermark and
  // skip state when the learner switches lessons.
  resetKey?: string | number;
  // How far ahead of the watermark currentTime may jump before it's
  // treated as a skip. Covers natural buffering/keyframe jumps without
  // tripping on a one-second stutter. Forward drag of more than this is a
  // deliberate skip.
  graceSeconds?: number;
}

export interface VideoSeekGuardState {
  // True while the learner has seeked past the furthest legitimately
  // watched point. Cleared once they're back at/behind the watermark.
  skipped: boolean;
  // Furthest point reached through normal playback ("watermark"), seconds.
  // This is both the "Watched: …" figure and the jump-back target.
  watchedSeconds: number;
  // Total media duration in seconds (0 until metadata loads).
  duration: number;
  // Seek the player back to the watermark and resume.
  jumpBack: () => void;
}

/**
 * Anti-skip guard for the learner video player. Tracks the furthest point
 * reached through normal playback and, when the learner seeks ahead of it,
 * pauses the video and surfaces a "jump back" affordance (see
 * VideoWatchGuard). Rewatching earlier content is always allowed — only
 * forward jumps past the watermark are blocked.
 *
 * Implementation note: we POLL `player.currentTime` rather than listen for
 * `timeupdate`/`seeked`. Mux-player (and other custom-element players)
 * don't reliably relay those events to host-element listeners — the same
 * quirk that drove useVideoWatchTracking to wall-clock tracking — but the
 * `currentTime`/`duration` *properties* are always readable, so a short
 * interval is the robust signal here.
 */
export function useVideoSeekGuard(
  playerRef: React.MutableRefObject<any>,
  options: Options,
): VideoSeekGuardState {
  const { enabled = true, resetKey, graceSeconds = 1.5 } = options;

  // Watermark: furthest second reached through legitimate playback.
  const watermarkRef = useRef(0);
  // Mirror of `skipped` for the interval closure (which is created once).
  const skippedRef = useRef(false);

  const [skipped, setSkipped] = useState(false);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);

  // Reset when the media changes.
  useEffect(() => {
    watermarkRef.current = 0;
    skippedRef.current = false;
    setSkipped(false);
    setWatchedSeconds(0);
    setDuration(0);
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) return;
    const player = playerRef.current;
    if (!player) return;

    const tick = () => {
      const current = Number(player.currentTime) || 0;
      const dur = Number(player.duration);
      if (dur && isFinite(dur) && dur > 0) setDuration(dur);

      const watermark = watermarkRef.current;
      if (current > watermark + graceSeconds) {
        // Forward skip — pause and flag. Pausing (rather than snapping
        // back) mirrors the Rise behavior the warning UI is modeled on:
        // the learner sees where they landed but can't advance from it.
        // If they press play again, the next tick re-pauses them.
        if (!skippedRef.current) {
          skippedRef.current = true;
          setSkipped(true);
        }
        // Pause only if actually playing — re-pausing an already-paused
        // element every tick would spam `pause` events at other listeners
        // (e.g., the completion tracker). This still catches the learner
        // pressing play again from the skipped position: the next tick
        // sees them playing and pauses once more.
        try {
          if (!player.paused) player.pause?.();
        } catch {
          /* player not ready / method absent — ignore */
        }
      } else {
        // At or behind the watermark. Advance it on forward progress and
        // clear any prior skip state (covers the learner scrubbing back
        // to a valid position on their own).
        if (current > watermark) {
          watermarkRef.current = current;
          setWatchedSeconds(current);
        }
        if (skippedRef.current) {
          skippedRef.current = false;
          setSkipped(false);
        }
      }
    };

    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [enabled, playerRef, resetKey, graceSeconds]);

  const jumpBack = () => {
    const player = playerRef.current;
    if (!player) return;
    try {
      player.currentTime = watermarkRef.current;
      skippedRef.current = false;
      setSkipped(false);
      player.play?.();
    } catch {
      /* seek/play unavailable — ignore */
    }
  };

  return { skipped, watchedSeconds, duration, jumpBack };
}
